export type ReportLevel = "fatal" | "error" | "warning" | "info";

export interface ReportOptions {
  /**
   * Stable identifier for the call site, e.g. `"session_token_bootstrap"`.
   */
  source: string;
  level?: ReportLevel;
  extra?: Record<string, unknown>;
}

/**
 * Telemetry shim. Sentry and PostHog were removed from sphinx; these keep the
 * same API surface so error boundaries and call sites stay untouched, and a
 * caught failure still lands in the browser console instead of vanishing.
 */
export function reportPossiblyOurFailure(
  _error: unknown,
  _options: ReportOptions,
): boolean {
  return false;
}

function toError(error: unknown): Error {
  if (error instanceof Error) return error;
  try {
    return new Error(typeof error === "string" ? error : JSON.stringify(error));
  } catch {
    return new Error(String(error));
  }
}

/**
 * An expected authorization refusal (an error carrying `data.kind ===
 * 'forbidden'`, the shape the backend used for application errors) is not a
 * defect — keep it out of the console noise.
 */
function isAuthorizationRefusal(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const data: unknown = (error as { data?: unknown }).data;
  return (
    typeof data === "object" &&
    data !== null &&
    (data as { kind?: unknown }).kind === "forbidden"
  );
}

/** Console-backed replacement for the old Sentry/PostHog dual capture. */
export function reportCaught(error: unknown, options: ReportOptions): void {
  if (isAuthorizationRefusal(error)) return;

  try {
    console.error(`[${options.source}]`, toError(error), options.extra ?? "");
  } catch {
    // Reporting must never escalate a failure into a second one.
  }
}

/**
 * Report an error caught by a React error boundary, preserving the component
 * stack. The boundary UI is unchanged; only the telemetry send is gone.
 */
export function reportBoundaryError(
  error: unknown,
  info: { componentStack?: string | null },
  name?: string,
): void {
  reportCaught(error, {
    source: name ? `react_boundary:${name}` : "react_boundary",
    extra: {
      componentStack: info?.componentStack ?? undefined,
      boundary: name ?? "unnamed",
    },
  });
}
